import { z } from 'zod';

import type {
  CreatePersonaRequest,
  Persona,
  PersonaStatus,
  UpdatePersonaRequest,
} from '~/modules/query/ai/types';

/** The service rejects a longer name; the create and detail forms say so before saving. */
export const MAX_NAME_LENGTH = 200;

/** The description is a one-line summary for the persona list. */
export const MAX_DESCRIPTION_LENGTH = 160;

export interface PersonaFormState {
  description: string;
  instructions: string;
  name: string;
  status: PersonaStatus;
}

export interface PersonaStatusOption {
  value: PersonaStatus;
  label: string;
  description: string;
}

export const PERSONA_STATUS_OPTIONS: readonly PersonaStatusOption[] = [
  {
    value: 'active',
    label: 'Active',
    description: 'Available to every placement that references this persona.',
  },
  {
    value: 'draft',
    label: 'Draft',
    description: 'Saved, but placements keep ignoring it until you activate it.',
  },
];

/** A new persona is active, matching the service default. */
export function emptyPersonaForm(): PersonaFormState {
  return { name: '', description: '', instructions: '', status: 'active' };
}

export function personaToFormState(persona: Persona): PersonaFormState {
  return {
    name: persona.name,
    description: persona.description ?? '',
    instructions: persona.instructions,
    status: persona.status,
  };
}

/**
 * The one persona validation schema, shared by the create and detail forms. It mirrors the
 * service's rules: name, description and instructions are all required, the name is capped at
 * 200 characters and the description at 160 so it still fits a table cell.
 */
export const personaSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Enter a name for this persona.')
    .max(MAX_NAME_LENGTH, `Shorten the name to ${MAX_NAME_LENGTH} characters or fewer.`),
  description: z
    .string()
    .trim()
    .min(1, 'Add a short description for the persona list.')
    .max(
      MAX_DESCRIPTION_LENGTH,
      `Shorten the description to ${MAX_DESCRIPTION_LENGTH} characters or fewer.`,
    ),
  instructions: z.string().trim().min(1, 'Write the instructions this persona follows.'),
});

/** The form as it is saved: every text field trimmed. */
export const trimPersonaForm = (form: PersonaFormState): PersonaFormState => ({
  ...form,
  name: form.name.trim(),
  description: form.description.trim(),
  instructions: form.instructions.trim(),
});

export function toCreatePersonaRequest(form: PersonaFormState): CreatePersonaRequest {
  return trimPersonaForm(form);
}

export function toUpdatePersonaRequest(id: string, form: PersonaFormState): UpdatePersonaRequest {
  return { id, ...toCreatePersonaRequest(form) };
}
